import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react"
import Image from "next/image"
import { useRouter } from "next/router"

import { useOrganization, useOrganizationList } from "@clerk/nextjs"
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  LoadingScreen,
} from "@coval/ui"

import {
  buildOrganizationHomePath,
  getRouteOrganizationSlug,
} from "@/utils/workspaceRoutes"

function OrganizationAccessError({
  organizationSlug,
  onBackHome,
  onChooseOrganization,
}: {
  organizationSlug: string
  onBackHome: () => void
  onChooseOrganization: () => void
}) {
  return (
    <div
      className="flex min-h-screen items-center justify-center bg-surface-default px-6"
      data-testid="organization-access-error"
    >
      <div className="w-full max-w-lg rounded-lg border border-coval-border bg-surface-0 p-6 text-center shadow-sm">
        <h1 className="text-lg font-semibold text-coval-text">
          We could not access this organization
        </h1>
        <p className="mt-2 text-sm text-coval-text-muted">
          Your account does not have access to {organizationSlug}. You can go
          back home or choose another organization.
        </p>
        <div className="mt-5 flex flex-col justify-center gap-3 sm:flex-row">
          <Button variant="secondary" onClick={onBackHome}>
            Back to home
          </Button>
          <Button variant="secondary" onClick={onChooseOrganization}>
            Choose another organization
          </Button>
        </div>
      </div>
    </div>
  )
}

function OrganizationAccessUnavailable({
  organizationSlug,
  onRetry,
  onBackHome,
}: {
  organizationSlug: string
  onRetry: () => void
  onBackHome: () => void
}) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-default px-6">
      <div className="w-full max-w-lg rounded-lg border border-coval-border bg-surface-0 p-6 text-center shadow-sm">
        <h1 className="text-lg font-semibold text-coval-text">
          We could not load this organization
        </h1>
        <p className="mt-2 text-sm text-coval-text-muted">
          Something went wrong checking access to {organizationSlug}. This is
          usually temporary.
        </p>
        <div className="mt-5 flex flex-col justify-center gap-3 sm:flex-row">
          <Button onClick={onRetry}>Try again</Button>
          <Button variant="secondary" onClick={onBackHome}>
            Back to home
          </Button>
        </div>
      </div>
    </div>
  )
}

function SwitchOrganizationConfirm({
  organizationName,
  organizationSlug,
  organizationImageUrl,
  onConfirm,
  onCancel,
}: {
  organizationName: string
  organizationSlug: string
  organizationImageUrl: string | null
  onConfirm: () => void
  onCancel: () => void
}) {
  return (
    <Dialog open onOpenChange={() => {}}>
      <DialogContent className="w-full max-w-md">
        <DialogHeader>
          <DialogTitle data-testid="switch-organization-confirm">
            Switch organization?
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div className="flex items-center gap-3">
            {organizationImageUrl ? (
              <div className="relative h-10 w-10 shrink-0 overflow-hidden rounded-full ring-2 ring-white/20 shadow-md">
                <Image
                  src={organizationImageUrl}
                  alt={organizationName}
                  fill
                  className="object-cover"
                />
              </div>
            ) : (
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-linear-to-br from-blue-500 via-purple-500 to-pink-500 text-sm font-bold text-white shadow-md ring-2 ring-white/20">
                {organizationName.charAt(0).toUpperCase()}
              </div>
            )}
            <div>
              <p className="text-sm font-medium text-coval-text">
                {organizationName}
              </p>
              <p className="text-xs text-coval-text-muted">
                This link will switch you to the {organizationSlug}{" "}
                organization.
              </p>
            </div>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onCancel}>
            Stay here
          </Button>
          <Button onClick={onConfirm}>Switch organization</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Activates the URL organization in Clerk before organization-scoped providers
 * and routes mount.
 */
export function RouteOrganizationGate({ children }: { children: ReactNode }) {
  const router = useRouter()
  const { isLoaded: organizationLoaded, organization } = useOrganization()
  const {
    isLoaded: membershipsLoaded,
    setActive,
    userMemberships,
  } = useOrganizationList({
    userMemberships: { infinite: true, pageSize: 100 },
  })
  const routeOrganizationSlug = useMemo(
    () =>
      (router.isReady ?? true) ? getRouteOrganizationSlug(router.asPath) : null,
    [router.asPath, router.isReady],
  )
  // A Clerk call that failed, as opposed to a confirmed absence of membership.
  // Kept separate so a transient failure offers a retry instead of latching the
  // route into a permanent "no access" screen.
  const [failureSlug, setFailureSlug] = useState<string | null>(null)
  const [validatedMembershipSlug, setValidatedMembershipSlug] = useState<
    string | null
  >(null)
  const [confirmedSwitchSlug, setConfirmedSwitchSlug] = useState<string | null>(
    null,
  )
  const activatingOrganizationIdRef = useRef<string | null>(null)
  const validatingMembershipSlugRef = useRef<string | null>(null)

  const routeMembership = routeOrganizationSlug
    ? userMemberships?.data?.find(
        (membership) => membership.organization.slug === routeOrganizationSlug,
      )
    : undefined
  const needsAnotherMembershipPage =
    routeOrganizationSlug !== null &&
    !routeMembership &&
    Boolean(userMemberships?.hasNextPage)
  const membershipsSettled =
    membershipsLoaded &&
    userMemberships?.data !== undefined &&
    !userMemberships.isFetching &&
    !needsAnotherMembershipPage &&
    validatedMembershipSlug === routeOrganizationSlug
  // Both halves have to match. Transport compares the active organization's id
  // as well as its slug, so a stale id under a matching slug leaves it waiting
  // for an activation the gate would otherwise decide was unnecessary — and
  // since transport no longer activates anything itself, nothing would ever
  // resolve it.
  const activeOrganizationMatchesRoute =
    routeOrganizationSlug !== null &&
    organization?.slug === routeOrganizationSlug &&
    (routeMembership === undefined ||
      organization?.id === routeMembership.organization.id)

  // The prompt exists to stop a link moving someone out of the organization
  // they are working in. With no active organization there is nothing to move
  // them out of, so asking is meaningless — and on /select-org they have just
  // chosen this organization explicitly. Confirming is only warranted when
  // there is an active organization to leave behind.
  const requiresSwitchConfirmation =
    organization != null && !activeOrganizationMatchesRoute

  // A revalidation that rejects after the user has navigated away still latches
  // its captured slug, which would greet them with the retry screen when they
  // next visit that organization. Leaving the route clears the latch, so the
  // failure only ever blocks the visit it happened on.
  useEffect(() => {
    validatingMembershipSlugRef.current = null
    activatingOrganizationIdRef.current = null
    setFailureSlug(null)
  }, [routeOrganizationSlug])

  useEffect(() => {
    if (
      activeOrganizationMatchesRoute &&
      confirmedSwitchSlug === routeOrganizationSlug
    ) {
      setConfirmedSwitchSlug(null)
    }
  }, [
    activeOrganizationMatchesRoute,
    confirmedSwitchSlug,
    routeOrganizationSlug,
  ])

  useEffect(() => {
    if (
      routeOrganizationSlug === null ||
      !membershipsLoaded ||
      userMemberships?.data === undefined ||
      userMemberships.isFetching ||
      validatedMembershipSlug === routeOrganizationSlug ||
      validatingMembershipSlugRef.current === routeOrganizationSlug
    ) {
      return
    }

    validatingMembershipSlugRef.current = routeOrganizationSlug
    void userMemberships.revalidate().then(
      () => {
        if (validatingMembershipSlugRef.current === routeOrganizationSlug) {
          validatingMembershipSlugRef.current = null
          setValidatedMembershipSlug(routeOrganizationSlug)
        }
      },
      () => {
        if (validatingMembershipSlugRef.current === routeOrganizationSlug) {
          validatingMembershipSlugRef.current = null
          setFailureSlug(routeOrganizationSlug)
        }
      },
    )
  }, [
    membershipsLoaded,
    routeOrganizationSlug,
    userMemberships,
    validatedMembershipSlug,
  ])

  useEffect(() => {
    if (
      needsAnotherMembershipPage &&
      !userMemberships?.isFetching &&
      userMemberships?.fetchNext
    ) {
      // Without this the page never settles: `needsAnotherMembershipPage` stays
      // true and the gate holds on the loading screen forever.
      Promise.resolve(userMemberships.fetchNext()).catch(() => {
        setFailureSlug(routeOrganizationSlug)
      })
    }
  }, [needsAnotherMembershipPage, routeOrganizationSlug, userMemberships])

  useEffect(() => {
    if (
      !routeMembership ||
      !organizationLoaded ||
      activeOrganizationMatchesRoute ||
      !setActive ||
      failureSlug === routeOrganizationSlug ||
      (requiresSwitchConfirmation &&
        confirmedSwitchSlug !== routeOrganizationSlug)
    ) {
      return
    }

    const organizationId = routeMembership.organization.id
    if (activatingOrganizationIdRef.current === organizationId) {
      return
    }

    activatingOrganizationIdRef.current = organizationId
    void setActive({
      organization: organizationId,
      redirectUrl: router.asPath,
    })
      .then(() => {
        if (activatingOrganizationIdRef.current === organizationId) {
          activatingOrganizationIdRef.current = null
        }
      })
      .catch(() => {
        if (activatingOrganizationIdRef.current === organizationId) {
          activatingOrganizationIdRef.current = null
        }
        setFailureSlug(routeOrganizationSlug)
      })
  }, [
    activeOrganizationMatchesRoute,
    confirmedSwitchSlug,
    failureSlug,
    organizationLoaded,
    requiresSwitchConfirmation,
    routeMembership,
    routeOrganizationSlug,
    router.asPath,
    setActive,
  ])

  // Clears the latches so the validation and activation effects run again.
  const retry = useCallback(() => {
    validatingMembershipSlugRef.current = null
    activatingOrganizationIdRef.current = null
    setFailureSlug(null)
    setValidatedMembershipSlug(null)
  }, [])

  if (!(router.isReady ?? true)) {
    return <LoadingScreen />
  }

  if (routeOrganizationSlug === null) {
    return children
  }

  // Ahead of the settled check: a failure means the membership list never
  // settles, and ahead of the access check because a failed lookup is not
  // evidence that the user lacks access.
  if (failureSlug === routeOrganizationSlug) {
    return (
      <OrganizationAccessUnavailable
        organizationSlug={routeOrganizationSlug}
        onRetry={retry}
        onBackHome={() => {
          void router.push("/home")
        }}
      />
    )
  }

  if (!organizationLoaded || !membershipsSettled) {
    return <LoadingScreen />
  }

  if (!routeMembership) {
    return (
      <OrganizationAccessError
        organizationSlug={routeOrganizationSlug}
        onBackHome={() => {
          void router.push("/home")
        }}
        onChooseOrganization={() => {
          void router.push("/select-org")
        }}
      />
    )
  }

  if (
    requiresSwitchConfirmation &&
    confirmedSwitchSlug !== routeOrganizationSlug
  ) {
    return (
      <>
        <LoadingScreen />
        <SwitchOrganizationConfirm
          organizationName={routeMembership.organization.name}
          organizationSlug={routeOrganizationSlug}
          organizationImageUrl={routeMembership.organization.imageUrl ?? null}
          onConfirm={() => {
            setConfirmedSwitchSlug(routeOrganizationSlug)
          }}
          onCancel={() => {
            if (organization?.slug) {
              void router.push(buildOrganizationHomePath(organization.slug))
            } else {
              void router.push("/select-org")
            }
          }}
        />
      </>
    )
  }

  if (!activeOrganizationMatchesRoute) {
    return <LoadingScreen />
  }

  return children
}
